import { withCustomRouter as withRouter } from '@/common/withCustomRouter';
import { useGetAuthUser } from '@/hooks/useGetAuthUser';
import { useIsClient } from '@/hooks/useIsClient';
import { useHeaderLayout } from '@/store/useHeaderLayout';
import { PRODUCT_NONE } from '@/utils/products';
import { Flex, themedColor, useViewportSize } from '@lemonade-hq/cdk';
import { type Language } from '@lemonade-hq/lemonation';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import {
  type AccountDropdownOptionsSettings,
  DEFAULT_FOOTER_LINKS,
  DEFAULT_HEADER_LINKS,
  Footer,
  type FooterProps,
  Header,
  type HeaderProps,
  NO_LINKS,
  ToggleButton,
  useAnalyticsContext,
  useI18nContext,
} from '@lemonade-hq/nuiza';
import { AnimatePresence, motion } from 'motion/react';
import dynamic from 'next/dynamic';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { BackToYourQuoteButton } from './BackToYourQuoteButton';
import { HeaderBanner, type HeaderBannerProps } from './HeaderBanner';
import JoinCTA from './JoinCTA';

const AssistantRenderer = dynamic(() => import('./AssistantRenderer').then(mod => mod.AssistantRenderer), {
  ssr: false,
});

export type ProductType = 'car' | 'life' | 'pet' | 'renters' | 'home';

type AccountDropDownOptionsSettingsObject = {
  [key in AccountDropdownOptionsSettings]: {
    title: string;
    url: string;
  };
};

type BackgroundColor = React.CSSProperties['backgroundColor'];
type Padding = React.CSSProperties['padding'] | React.CSSProperties['padding'][] | number[];

const ContentContainer = styled(Flex)`
  min-height: 100vh;
  ${({ contentBackground }: { contentBackground: BackgroundColor }) => `background-color: ${contentBackground};`}
`;

interface StyledHeaderProps {
  name?: string;
  headerColor?: BackgroundColor;
  sticky?: boolean;
}

const StyledHeader = styled(Header)<StyledHeaderProps>`
  ${({ headerColor }: StyledHeaderProps) => `background-color: ${headerColor};`}
  ${({ sticky }: StyledHeaderProps) =>
    !sticky &&
    css`
      position: absolute;

      ${ToggleButton} {
        position: absolute;
      }
    `}
`;

interface StickyHeaderWrapperProps {
  $sticky?: boolean;
}

const StickyHeaderWrapper = styled.div<StickyHeaderWrapperProps>`
  width: 100%;
  z-index: 100;
  display: flex;
  flex-direction: column;
  ${({ $sticky }) =>
    $sticky
      ? css`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
        `
      : css`
          position: absolute;
          left: 0;

          ${ToggleButton} {
            position: absolute;
          }
        `}
`;

const NonStickyStyledHeader = styled(Header)<StyledHeaderProps>`
  ${({ headerColor }: StyledHeaderProps) => `background-color: ${headerColor};`}
  position: relative;
  order: 1;

  ${ToggleButton} {
    top: 50px;
  }
`;

export const CheckOurPricesCTA = styled(JoinCTA)`
  display: block;

  &&& {
    text-decoration: none;
    padding: 12px 10px;
    line-height: 1.4rem;
    &:before {
      content: attr(data-title-mobile);
    }
    ${MEDIA_QUERIES.desktop`
            padding: 12px 30px;
            &:before {
                content: attr(data-title-desktop);
            }
        `}
    ${MEDIA_QUERIES.upToDesktop`
            font-size: 1.4rem !important;
            color: ${themedColor('white')} !important;
            background: ${themedColor('primary')} !important;
            box-shadow: 0 11px 17px -12px ${themedColor('primary')} !important;
        `}
  }
`;

const SkipLink = styled.a`
  position: absolute;
  top: -40px;
  left: 0;
  background: ${themedColor('primary')};
  color: ${themedColor('white')};
  padding: 10px 15px;
  z-index: 9999; // Ensure it's on top
  text-decoration: none;
  font-weight: bold;
  &:focus {
    top: 0;
  }
`;

const CheckOurPricesCTAContainer = styled(motion.div)`
  position: fixed;
  display: block;
  bottom: env(safe-area-inset-bottom, 0px);
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  padding: 0px 20px 24px 20px;

  a {
    padding-top: 15px !important;
    padding-bottom: 15px !important;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none !important;
  `}
`;

interface HeaderActionsLabels {
  customUrl?: string;
  long: {
    singular: string;
    plural: string;
  };
  short: {
    singular: string;
    plural: string;
  };
}

export interface LayoutProps extends Pick<HeaderProps, 'alignLogoOnMobile'> {
  hideSkipLink?: boolean;
  showAskUsAnything?: boolean;
  CustomHeaderCTA?: React.ReactNode;
  product?: string;
  showLogo?: boolean;
  fixedHeader?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  additionalFooterContent?: React.ReactElement;
  headerColor?: BackgroundColor | null;
  stickyHeader?: boolean;
  contentBackground?: BackgroundColor | null;
  pt?: Padding | null;
  pb?: Padding | null;
  alignItems?: React.CSSProperties['alignItems'] | null;
  overflow?: React.CSSProperties['overflow'];
  position?: React.CSSProperties['position'] | null;
  uuid?: string;
  children: React.ReactNode;
  showCTA?: boolean;
  showUserAccountComponent?: boolean;
  showBackToYourQuote?: boolean;
  className?: string;
  showDisclaimer?: boolean;
  disableLogoLink?: boolean;
  showBurgerMenu?: boolean;
  noLinks?: boolean;
  productType?: ProductType;
  page?: string;
  extraFooterDisclaimer?: React.ReactNode;
  headerActions: {
    dropdown: {
      myAccount: string;
      settings: string;
      logout: string;
    };
    CTAs: {
      backToYourQuote: HeaderActionsLabels;
      checkOurPrices: HeaderActionsLabels;
    };
  };
  headerBanner?: HeaderBannerProps;
  customHeader?: React.ReactNode;
  menuCheckPricesExtraAnalyticsParams?: Record<string, string>;
  floatingCheckOurPricesInMobile?: boolean;
}

const skipTranslations: Record<Language, string> = {
  en: 'Skip to main content',
  fr: 'Aller au contenu principal',
  de: 'Zum Hauptinhalt springen',
  nl: 'Spring naar de inhoud',
  es: 'Saltar al contenido principal',
};

export const AnimatedBottomStickyCheckOurPricesCTAContainer: React.FC<React.PropsWithChildren<{ show: boolean }>> = ({
  children,
  show,
}) => {
  return (
    <AnimatePresence>
      {show && (
        <CheckOurPricesCTAContainer
          animate={{
            opacity: [0, 1],
            y: [40, 0],
          }}
          exit={{
            opacity: [1, 0],
            y: [0, 40],
          }}
          transition={{
            duration: 0.15,
          }}
        >
          {children}
        </CheckOurPricesCTAContainer>
      )}
    </AnimatePresence>
  );
};

const Layout: React.FC<LayoutProps> = ({
  hideSkipLink = false,
  showAskUsAnything = false,
  CustomHeaderCTA = null,
  product = '',
  showLogo = true,
  fixedHeader = false,
  showHeader = true,
  showFooter = true,
  additionalFooterContent,
  headerColor = null,
  stickyHeader = true,
  contentBackground = null,
  pt = [70, 60, 120],
  pb = null,
  alignItems = null,
  overflow = 'hidden',
  position = 'static',
  uuid = '',
  children,
  showCTA = true,
  showUserAccountComponent = true,
  showBackToYourQuote = true,
  className = '',
  showDisclaimer = true,
  disableLogoLink = false,
  showBurgerMenu = true,
  noLinks = false,
  productType,
  extraFooterDisclaimer,
  headerActions,
  page = '',
  headerBanner,
  customHeader,
  alignLogoOnMobile = 'center',
  menuCheckPricesExtraAnalyticsParams,
  floatingCheckOurPricesInMobile = true,
}) => {
  const isClient = useIsClient();
  const [showAdditionalHeaderCTA, showLogin] = useHeaderLayout(s => [s.showAdditionalHeaderCTA, s.showLogin]);
  const { lang } = useI18nContext();

  const trackingProductType = productType ?? PRODUCT_NONE;

  const { user, status: userRequestStatus } = useGetAuthUser();
  const trackingContext = 'marketing_website';
  const { trackEvent } = useAnalyticsContext();

  const showUserAccount = useMemo(() => {
    return showUserAccountComponent && user?.userFirstName != null;
  }, [showUserAccountComponent, user]);

  const overrideHeaderLinks = noLinks ? NO_LINKS : DEFAULT_HEADER_LINKS;

  const trackClick = (): void => {
    const currentProduct = (): string => {
      if (product.includes('renters')) return 'renters';
      if (product.includes('owners')) return 'home';
      return product;
    };

    trackEvent('back_to_quote.clicked', {
      uuid,
      quote_id: user?.quoteId ?? '',
      product: currentProduct(),
      destination: user?.showCart != null && user?.cartPath != null ? 'checkout' : 'quote_page',
    });

    if (!isClient) return;

    if (user?.showCart && user?.cartPath) {
      window.location.href = user.cartPath;
    } else if (user?.quotePath) {
      window.location.href = user.quotePath;
    }
  };

  const extraAnalyticsParamsCheckOurPricesEvent = useMemo(() => {
    if (menuCheckPricesExtraAnalyticsParams != null) {
      return Object.entries(menuCheckPricesExtraAnalyticsParams).reduce(
        (acc, [key, value]) => {
          acc[`data-event-param-${key}`] = value;
          return acc;
        },
        {} as Record<string, string>,
      );
    }
    return {};
  }, [menuCheckPricesExtraAnalyticsParams]);

  const checkOurPricesCtaProps = {
    product,
    size: 'small',
    flavor: 'primary',
    glowing: true,
    'aria-label': headerActions.CTAs.checkOurPrices.long.plural,
    customUrl: headerActions.CTAs.checkOurPrices.customUrl,
    'data-event': 'menu_check_prices',
    'data-source': product,
    'data-position': floatingCheckOurPricesInMobile ? 'bottom' : 'header',
    'data-event-param-product': product,
    'data-title-desktop': headerActions.CTAs.checkOurPrices.long.plural,
    'data-title-mobile': headerActions.CTAs.checkOurPrices.short.plural,
    ...extraAnalyticsParamsCheckOurPricesEvent,
  } as const;

  const showBackToQuoteCTA = user?.quotePath && showBackToYourQuote;
  const showFloatingMobileCheckOurPricesCTA =
    showCTA && isClient && !showBackToQuoteCTA && floatingCheckOurPricesInMobile;

  const DefaultCTA =
    showCTA &&
    isClient &&
    (showBackToQuoteCTA ? (
      <BackToYourQuoteButton
        trackClick={trackClick}
        titleSmall={
          user?.showCart && (user?.cartCount ?? 0) > 1
            ? headerActions.CTAs.backToYourQuote.short.plural
            : headerActions.CTAs.backToYourQuote.short.singular
        }
        titleLarge={
          user?.showCart && (user?.cartCount ?? 0) > 1
            ? headerActions.CTAs.backToYourQuote.long.plural
            : headerActions.CTAs.backToYourQuote.long.singular
        }
      />
    ) : (
      <CheckOurPricesCTA {...checkOurPricesCtaProps}>{null}</CheckOurPricesCTA>
    ));

  const accountDropDownOptionsSettings: AccountDropDownOptionsSettingsObject = {
    MyAccount: {
      title: headerActions.dropdown.myAccount ?? '',
      url: user?.myAccountPath ?? '',
    },
    Settings: {
      title: headerActions.dropdown.settings,
      url: user?.settingsPath ?? '',
    },
    Logout: {
      title: headerActions.dropdown.logout,
      url: user?.logOutPath ?? '',
    },
  };

  const renderProduct = (): ProductType => {
    if (product === 'pet') return 'pet';
    if (product === 'life') return 'life';
    if (product.includes('renters')) return 'renters';
    if (product.includes('owners')) return 'home';
    return 'car';
  };

  const showAdditinalCta = useMemo(() => {
    return showCTA && (user?.quotePath != null || showAdditionalHeaderCTA);
  }, [showCTA, user?.quotePath, showAdditionalHeaderCTA]);

  const { isMobileViewport } = useViewportSize();

  const additionalCTA =
    showFloatingMobileCheckOurPricesCTA && isMobileViewport ? null : (CustomHeaderCTA ?? DefaultCTA);

  const headerElement = (
    <StyledHeader
      name="header"
      sticky={stickyHeader}
      fixed={fixedHeader}
      showLogin={showLogin && !showUserAccount && userRequestStatus !== 'loading'}
      showLogo={showLogo}
      showUserAccount={showUserAccount}
      routerHoc={withRouter as HeaderProps['routerHoc']}
      {...(headerColor && { headerColor })}
      additionalCTA={additionalCTA}
      showAdditionalCTA={showAdditinalCta}
      firstName={userRequestStatus === 'loading' ? '' : user?.userFirstName}
      lastName={userRequestStatus === 'loading' ? '' : user?.userLastName}
      trackingContext={trackingContext}
      accountDropdownOptionsSettings={accountDropDownOptionsSettings}
      disableLogoLink={disableLogoLink}
      showBurgerMenu={showBurgerMenu}
      links={overrideHeaderLinks}
      alignLogoOnMobile={alignLogoOnMobile}
    />
  );

  const headerWithBanner = (
    <StickyHeaderWrapper $sticky={stickyHeader}>
      <HeaderBanner {...headerBanner!} />
      <NonStickyStyledHeader
        name="header"
        fixed={fixedHeader}
        showLogin={showLogin && !showUserAccount && userRequestStatus !== 'loading'}
        showLogo={showLogo}
        showUserAccount={showUserAccount}
        routerHoc={withRouter as HeaderProps['routerHoc']}
        {...(headerColor && { headerColor })}
        additionalCTA={additionalCTA}
        showAdditionalCTA={showCTA && (user?.quotePath != null || showAdditionalHeaderCTA)}
        firstName={userRequestStatus === 'loading' ? '' : user?.userFirstName}
        lastName={userRequestStatus === 'loading' ? '' : user?.userLastName}
        trackingContext={trackingContext}
        accountDropdownOptionsSettings={accountDropDownOptionsSettings}
        disableLogoLink={disableLogoLink}
        showBurgerMenu={showBurgerMenu}
        links={overrideHeaderLinks}
        alignLogoOnMobile={alignLogoOnMobile}
      />
    </StickyHeaderWrapper>
  );

  return (
    <>
      {!hideSkipLink && <SkipLink href="#main-content">{skipTranslations[lang]}</SkipLink>}
      {showHeader && customHeader == null && (headerBanner != null ? headerWithBanner : headerElement)}
      {customHeader != null && customHeader}
      <ContentContainer
        {...(contentBackground != null && { contentBackground })}
        flexDirection="column"
        className={className}
      >
        <Flex
          as="main"
          role="main"
          id="main-content"
          flexDirection="column"
          flex="auto"
          pt={pt}
          pb={pb}
          alignItems={alignItems}
          overflow={overflow}
          position={position}
          height="auto"
        >
          {children}
        </Flex>
        {showFooter && (
          <Footer
            product={productType ?? renderProduct()}
            routerHoc={withRouter as FooterProps['routerHoc']}
            showDisclaimer={showDisclaimer}
            extraDisclaimer={extraFooterDisclaimer}
            links={DEFAULT_FOOTER_LINKS}
          />
        )}
        {additionalFooterContent}
      </ContentContainer>

      <AnimatedBottomStickyCheckOurPricesCTAContainer
        show={showFloatingMobileCheckOurPricesCTA && showAdditionalHeaderCTA}
      >
        {CustomHeaderCTA ?? DefaultCTA}
      </AnimatedBottomStickyCheckOurPricesCTAContainer>
      {showAskUsAnything && <AssistantRenderer product={trackingProductType} page={page} />}
    </>
  );
};

export default React.memo(Layout);
